import { useCallback, useEffect, useState } from "react";

import { addMinutes, differenceInMinutes, format, parseISO } from "date-fns";
import { useQuery } from "react-query";
import { api } from "../../config";
import { useSelector } from "react-redux";
import { AuthenticationState, UserInterface } from "../../interfaces/users";
import {
  Badge,
  Button,
  Drawer,
  Form,
  Modal,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import { toast } from "react-toastify";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import SuggestSchedule from "./SuggestSchedule/SuggestSchedule";
import AgendaTimeline from "../Shared/Components/AssemblersAgenda/AgendaTimeline";
import TicketDrawer from "../Shared/Components/TicketDrawer";
import Link from "antd/es/typography/Link";
import ScheduleAssembleEvent from "../Shared/Components/ScheduleAssembleEvent";
import { EventAssembleInterface } from "../../interfaces/eventAssemble";
import {
  formatSeniority,
  getProductsAssembleTime,
  getProductsSeniority,
} from "../../utils/helpers/ticketHelpers";
import { SearchAssemblers } from "../Shared/Components/SearchAssemblers/SearchAssemblers";
import { getColumnSearchProps } from "./SuggestSchedule/searchFieldProps";
import { useTableSearch } from "./SuggestSchedule/searchContext";
import { useModal } from "../../utils/hooks/useModal";
import { SingleSchedule } from "./SingleSchedule";
import { differenceInCalendarDays } from "date-fns/fp";
import {
  getAssemblyLineType,
  getAssemblyTimelineEventClassName,
} from "../../utils/helpers/assemblyTimelineEventHelpers";
import RelatedPendingScheduleTicketsModal from "../Shared/Components/RelatedPendingScheduleTicketsModal";

export const printSchedule = async (ticketId: number, token: string) => {
  try {
    const response = await api(token).get(`schedule/sale/${ticketId}`);

    // Garantir que o dado retornado é só o base64
    const base64 = response;

    // Converte base64 para ArrayBuffer
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ticket-${ticketId}.pdf`; // Nome do arquivo
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.log(error);
  }
};

export const AssemblyCalendar = () => {
  const { token } = useSelector((state: AuthenticationState) => state);
  const [assembler, setAssembler] = useState<number>();
  const {
    data: events,
    isFetching: isFetchingEvents,
    refetch: refetchEvents,
  } = useQuery<EventAssembleInterface[]>("events-assemble", () =>
    api(token).get(
      `events/assemble?` + (assembler ? `&assemblerId=${assembler}` : "")
    )
  );

  const {
    data: users,
    isFetching: isFetchingUsers,
    refetch: refetchUsers,
  } = useQuery<UserInterface[]>("users-assemble-calendar", () =>
    api(token).get(
      `/users?moduleName=ASSEMBLE_APP&permission=USER` +
        (assembler ? `&ids=${assembler}` : "")
    )
  );

  const [groups, setGroups] = useState<
    { id: number; title: string; seniorityId: number }[]
  >([]);

  const [items, setItems] = useState<any[]>([]);

  const [tickets, setTickets] = useState<{ tickets: any[]; total: number }>({
    tickets: [],
    total: 0,
  });

  const [openTicketDrawer, setOpenTicketDrawer] = useState(false);
  const [openAgendaDrawer, setOpenAgendaDrawer] = useState(false);
  const { isOpen, onClose, onOpen } = useModal();
  const [openAgendaModal, setOpenAgendaModal] = useState(false);
  const [openSuggestionModal, setOpenSuggestionModal] = useState(false);

  const [drawerTicket, setDrawerTicket] = useState<any>();
  const [agendaTickets, setAgendaTickets] = useState<any>([]);
  const [agendaModalTicket, setAgendaModalTicket] = useState<any>();
  const [relatedScheduleTaxId, setRelatedScheduleTaxId] = useState<
    string | undefined
  >();
  const [relatedScheduleExcludeId, setRelatedScheduleExcludeId] = useState<
    number | undefined
  >();
  const [showRelatedScheduleModal, setShowRelatedScheduleModal] =
    useState(false);

  const [newEventId, setNewEventId] = useState<any>();
  const [selectedEvent, setSelectedEvent] = useState<any>();

  const [defaultScheduleTime, setDefaultScheduleTime] = useState<any>(0);

  const [actualAgendaView, setActualAgendaView] = useState<any>("week");
  const [movingEventId, setMovingEventId] = useState<number | string>();
  const agendaCollumns: TableColumnsType<any> = [
    {
      title: "O.S.",
      dataIndex: "title",
      key: "title",
      sorter: (a: any, b: any) => stringSorter(a.title, b.title),
      ...getColumnSearchProps("title"),
      filterSearch: true,
      render: (title: any, record: any) => (
        <Tooltip
          title={() => (
            <div className="w-fit">
              <div>Produtos: </div>
              {record.products?.map((product: any) => (
                <div
                  style={{ marginBottom: "4px" }}
                  key={product.id}
                  className="flex justify-between"
                >
                  <span style={{ lineHeight: "12px" }}>
                    {product.product.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        >
          <Link href={`/montagem?id=${record.id}`} target="_blank">
            <b>{title.split(" | ")[0].replace(/\D+/, "")}</b>
          </Link>
        </Tooltip>
      ),
    },
    {
      title: "ID da Venda",
      dataIndex: "saleId",
      key: "saleId",
      sorter: true,

      ...getColumnSearchProps("saleId"),
      filterSearch: true,
    },
    {
      title: "Tipo de Linha",
      dataIndex: "assemblyLineType",
      key: "assemblyLineType",
      render: (assemblyLineType: string) => (
        <span
          className="inline-flex w-fit items-center rounded-full px-3 py-0.5 text-xs font-semibold text-white shadow"
          style={{
            backgroundColor:
              assemblyLineType === "Linha Planejada" ? "#722ed1" : "#01c558",
          }}
        >
          {assemblyLineType}
        </span>
      ),
    },
    {
      title: "Situação",
      dataIndex: "isUnavailableForAssemble",
      key: "isUnavailableForAssemble",
      render: (isUnavailableForAssemble: boolean) =>
        isUnavailableForAssemble ? (
          <Tag color="orange">Em obras/indisponível</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Nome Do Cliente - CPF/CNPJ",
      dataIndex: ["client", "name"],
      key: "client_name",
      sorter: true,
      render: (client_name: any, record: any) => <b>{client_name}</b>,
      ...getColumnSearchProps("client_name"),
      filterSearch: true,
    },
    {
      title: "Bairro",
      showSorterTooltip: { target: "full-header" },
      dataIndex: ["client", "deliveryAddress"],
      key: "address_neighborhood",
      render: (address: any) => address?.neighbor,
      sorter: (a: any, b: any) =>
        stringSorter(a.client.address?.neighbor, b.client.address?.neighbor),
      filters: Array.from(
        new Set([
          ...agendaTickets.map(
            (ticket: any) => ticket.client.address?.neighbor
          ),
        ])
      )
        .filter((neighbor: any) => neighbor)
        .map((neighbor: any) => ({
          text: neighbor,
          value: neighbor,
        })),
      ...getColumnSearchProps("address_neighborhood"),

      onFilter: (value: any, record: any) =>
        record?.client?.address?.neighbor?.startsWith(value as string),
      filterSearch: true,
    },
    {
      title: "Cidade",
      dataIndex: ["client", "deliveryAddress"],
      key: "address_city",
      render: (address: any) => address?.city,
      sorter: true,
      filters: Array.from(
        new Set([
          ...agendaTickets.map((ticket: any) => ticket.client.deliveryAddress?.city),
        ])
      )
        .filter((city: any) => city)
        .map((city: any) => ({
          text: city,
          value: city,
        })),
      ...getColumnSearchProps("address_city"),

      filterSearch: true,
    },
    {
      title: "Tempo Parado",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: any) => {
        const difference = differenceInCalendarDays( new Date(createdAt), new Date())
        return <p style={{color: difference > 20 ? 'red' : difference > 10 ? 'orange' :  'unset'}}>{`${difference} dia(s)`}</p>},
      // sorter: (a: any, b: any) =>
      //   stringSorter(a.client.address?.state, b.client.address?.state),
      // filters: Array.from(
      //   new Set([
      //     ...agendaTickets.map((ticket: any) => ticket.client.address?.state),
      //   ])
      // ).map((state: any) => ({
      //   text: state,
      //   value: state,
      // }))
      onFilter: (value: any, record: any) =>
        record?.client?.address?.state
          ?.toLowerCase()
          .includes(value?.toLowerCase()),
      filterSearch: true,
            sorter: true
      // ...getColumnSearchProps("createdAt"),
    },
    // {
    //   title: "Estado",
    //   dataIndex: ["client", "deliveryAddress"],
    //   key: "address_state",
    //   render: (address: any) => address?.state,
    //   sorter: (a: any, b: any) =>
    //     stringSorter(a.client.address?.state, b.client.address?.state),
    //   filters: Array.from(
    //     new Set([
    //       ...agendaTickets.map((ticket: any) => ticket.client.address?.state),
    //     ])
    //   ).map((state: any) => ({
    //     text: state,
    //     value: state,
    //   })),
    //   onFilter: (value: any, record: any) =>
    //     record?.client?.address?.state
    //       ?.toLowerCase()
    //       .includes(value?.toLowerCase()),
    //   filterSearch: true,
    // },
    {
      title: "Valor da Montagem",
      dataIndex: "assemblyValue",
      key: "assemblyValue",
      render: (assemblyValue: any) => `R$ ${assemblyValue}`,
      sorter: true,
    },
    {
      title: "Senioridade",
      dataIndex: "seniority",
      key: "seniority",
      render: (seniority: any) => formatSeniority(seniority),
      sorter: (a: any, b: any) => b.seniority - a.seniority,
      filters: Array.from(
        new Set([...agendaTickets.map((ticket: any) => ticket.seniority)])
      ).map((seniority: any) => ({
        text: formatSeniority(seniority),
        value: seniority,
      })),
      onFilter: (value: any, record: any) => record.seniority === value,
      filterSearch: true,
    },
    {
      title: "Tempo de Montagem",
      dataIndex: "period",
      key: "period",
      render: (period: any) => `${period} horas`,
      sorter: (a: any, b: any) => a.period - b.period,
      filters:
        Array.from(
          new Set([...agendaTickets.map((ticket: any) => ticket.period)])
        ).map((period: any) => ({
          text: `${period} horas`,
          value: period,
        })) || [],
      onFilter: (value: any, record: any) => record.period === value,
      filterSearch: true,
    },
    {
      title: "Agendar",
      dataIndex: "id",
      key: "schedule",
      render: (ticketId: any, record: any) => (
        <Space size="middle">
          {record.errors.length ? (
            <Tooltip
              title={record.errors.map((error: string) => (
                <div>
                  {error}
                  <br />
                </div>
              ))}
              color={"red"}
              arrow={false}
            >
              <Badge
                count={
                  <ExclamationCircleOutlined
                    style={{ color: "#f5222d", fontSize: 16 }}
                  />
                }
              >
                <Button type="primary" disabled>
                  Agendar
                </Button>
              </Badge>
            </Tooltip>
          ) : (
            <Button type="primary" onClick={() => onClickAgendar(ticketId)}>
              Agendar
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const stringSorter = (a: string, b: string) => a.localeCompare(b);

  const onClickAgendar = (ticketId: any, time?: number) => {
    const ticket = tickets.tickets.find(
      (ticket: any) => ticket.id === ticketId
    );

    if (!ticket) return;

    if (ticket.status !== "ASSEMBLE_PENDING_SCHEDULE") {
      toast.error("O ticket selecionado precisa estar pendente de agendamento");

      return;
    }

    if (time) {
      setDefaultScheduleTime(new Date(time));
    }

    setAgendaModalTicket(ticket);
    setOpenAgendaDrawer(false);
    setOpenAgendaModal(true);
  };

  const onEventDoubleClick = (
    eventId: any,
    ticketId: any,
    time: number,
    groupOrder: number
  ) => {
    if (!eventId || !users) return;
    setNewEventId(eventId);

    if (ticketId) {
      setDrawerTicket(ticketId);
    } else {
      setSelectedEvent(events?.find((event) => eventId === event.id));
    }
    const selectedAgendaEvent = items.find((event: any) => event.id === eventId);
    if (selectedAgendaEvent) {
      setSelectedEvent(selectedAgendaEvent);
    }
    setOpenTicketDrawer(true);
  };

  const searchTickets = async () => {
    refetchEvents();
    refetchUsers();
  };

  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    searchText,
    searchedColumn,
    handleResetFilters,
    specificFilter,
    setSpecificFilter,
  } = useTableSearch();
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [tableResetKey, setTableResetKey] = useState(0);
  const [sortingOrder, setSortingOrder] = useState({
    field: "",
    order: "",
  });
  const fetchTickets = useCallback(async () => {
    try {
      setTicketsLoading(true);
      const response = await api(token).get(
        `tickets/assemble?status=ASSEMBLE_PENDING_SCHEDULE&page=${currentPage}&pageSize=${pageSize}${
          searchText ? `&${searchedColumn}=${searchText}` : ""
        }${
          sortingOrder.field
            ? `&orderBy=${sortingOrder.field}-${sortingOrder.order}`
            : `&orderBy=createdAt-asc`
        }${specificFilter ? `&specificFilter=${specificFilter}` : ''}`
      );
      setTickets(response);
      setAgendaTickets([]);
      populateAgenda(response);
    } catch (error) {
      console.log(error);
      toast.error("Ocorreu um erro ao Coletar as informações do servidor");
    } finally {
      setTicketsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    searchText,
    searchedColumn,
    sortingOrder.field,
    sortingOrder.order,
    token,
    specificFilter,
  ]);

  const applySpecificFilter = (filter: string) => {
    setCurrentPage(1);
    setSpecificFilter(filter);
  };

  const resetAgendaFilters = () => {
    handleResetFilters();
    setSortingOrder({ field: "", order: "" });
    setTableResetKey((currentKey) => currentKey + 1);
  };

  useEffect(() => {
    fetchTickets();
  }, [token, fetchTickets]);

  useEffect(() => {
    if (users && !isFetchingEvents && !isFetchingUsers) {
      populateItems(events);
      populateGroups(users);
    }
  }, [users, isFetchingUsers, isFetchingEvents, events]);

  const refetchAllTickets = () => {
    setOpenSuggestionModal(false);
    setItems([]);
    refetchEvents();
  };

  const refreshCalendarData = async () => {
    await Promise.all([refetchEvents(), fetchTickets()]);
  };

  const populateGroups = (data: any) => {
    const newGroups: { id: number; title: string; seniorityId: number }[] = [];
    data
      .filter((user: any) => user.seniorityId)
      .forEach((user: any) => {
        newGroups.push({
          id: user.id,
          title: user.fullName,
          seniorityId: user.seniorityId,
        });
      });

    setGroups(newGroups);
  };
  const populateItems = (data: any) => {
    const newItems = data.map((event: any) => {
      const seniorityId = getProductsSeniority(event.fieldReport?.products)
        ? getProductsSeniority(event.fieldReport.products)
        : 3;
      return {
        ...event,
        group: event.assemblerId,
        title: event.description,
        seniorityId,
        className: getAssemblyTimelineEventClassName(event),
        start_time: parseISO(event.start),
        end_time: parseISO(event.end),
      };
    });
    setItems(() => [...newItems]);
  };

  const populateAgenda = (data: any) => {
    const newItems = data.tickets
      .filter((item: any) => item.status === "ASSEMBLE_PENDING_SCHEDULE")
      .filter((item: any) => item.products?.length > 0)
      .map((item: any, index: any) => {
        const errors = [];

        if (
          !getProductsAssembleTime(item.products) ||
          !getProductsSeniority(item.products)
        ) {
          errors.push("Produto não cadastrado");
        } else {
          for (const product of item.products) {
            if (!product.product.seniorityId || !product.product.assemblyTime) {
              errors.push("Produtos não cadastrados");
              break;
            }
          }

          if (item?.products?.length === 0) {
            errors.push("Produtos não cadastrados");
          }
        }

        return {
          id: item.id,
          group: item.ownerId,
          title: item.title,
          owner: item.owner,
          status: item.status,
          client: item.client,
          notes: item.notes,
          saleId: item.saleId === 1111111 ? "AVULSA" : item.saleId,
          assemblyValue: (
            item.products.reduce(
              (total: any, product: any) => total + product.value,
              0
            ) || 0
          ).toFixed(2),
          period: getProductsAssembleTime(item.products),
          seniorityId: getProductsSeniority(item.products),
          seniority: getProductsSeniority(item.products),
          products: item.products,
          assemblyLineType: getAssemblyLineType(item.products),
          isUnavailableForAssemble: item.isUnavailableForAssemble,
          createdAt: item.createdAt,
          errors,
        };
      });

    setAgendaTickets(newItems);
  };

  const handleOk = () => {
    setOpenAgendaModal(false);
    refetchAllTickets();
  };

  const handleAfterSchedule = (scheduledTicket: any) => {
    setRelatedScheduleTaxId(scheduledTicket?.client?.taxId);
    setRelatedScheduleExcludeId(scheduledTicket?.id);
    setShowRelatedScheduleModal(true);
  };

  const onSuccessUpdateFront = (event: any) => {
    setItems([...items, event]);
  };

  const getEventDate = (date: Date | string) =>
    date instanceof Date ? date : parseISO(date);

  const getAssemblerName = (assemblerId?: number) =>
    groups.find((group) => group.id === assemblerId)?.title ??
    "Montador não informado";

  const formatScheduleDate = (date: Date) => format(date, "dd/MM/yyyy HH:mm");

  const rollbackTimelineChange = () => {
    setItems((currentItems) => [...currentItems]);
  };

  const hasScheduleOverlap = (
    eventId: number,
    nextStart: Date,
    nextEnd: Date,
    assemblerId: number
  ) =>
    items
      .filter((event: any) => event.id !== eventId)
      .filter((event: any) => event.assemblerId === assemblerId)
      .some((event: any) => {
        const eventStart = getEventDate(event.start_time ?? event.start);
        const eventEnd = getEventDate(event.end_time ?? event.end);

        return nextStart < eventEnd && nextEnd > eventStart;
      });

  const saveChangedEvent = async (
    event: any,
    nextStart: Date,
    nextEnd: Date,
    nextAssemblerId: number
  ) => {
    try {
      setMovingEventId(event.id);
      await api(token).put(`events/assemble/${event.id}`, {
        start: nextStart,
        end: nextEnd,
        assemblerId: nextAssemblerId,
      });
      setItems((currentItems) =>
        currentItems.map((currentEvent: any) =>
          currentEvent.id === event.id
            ? {
                ...currentEvent,
                assemblerId: nextAssemblerId,
                group: nextAssemblerId,
                start: nextStart,
                end: nextEnd,
                start_time: nextStart,
                end_time: nextEnd,
              }
            : currentEvent
        )
      );
      toast.success("Agendamento reagendado com sucesso!");
    } catch (error) {
      console.log(error);
      toast.error("Ocorreu um erro ao reagendar o ticket");
      rollbackTimelineChange();
    } finally {
      setMovingEventId(undefined);
    }
  };

  const confirmScheduleChange = ({
    event,
    nextStart,
    nextEnd,
    nextAssemblerId,
    title,
  }: {
    event: any;
    nextStart: Date;
    nextEnd: Date;
    nextAssemblerId: number;
    title: string;
  }) => {
    const currentStart = getEventDate(event.start_time ?? event.start);
    const currentEnd = getEventDate(event.end_time ?? event.end);
    const currentAssemblerId = event.assemblerId;
    const numericEventId = Number(event.id);

    if (nextStart >= nextEnd) {
      toast.error("O horário final deve ser maior que o horário inicial");
      rollbackTimelineChange();
      return;
    }

    if (
      hasScheduleOverlap(numericEventId, nextStart, nextEnd, nextAssemblerId)
    ) {
      toast.warning("Este horário sobrepõe outro evento do montador");
      rollbackTimelineChange();
      return;
    }

    rollbackTimelineChange();

    Modal.confirm({
      title,
      content: (
        <div>
          <p>Confirme a alteração do agendamento:</p>
          <p>
            <b>Montador:</b> {getAssemblerName(currentAssemblerId)} {" -> "}
            {getAssemblerName(nextAssemblerId)}
          </p>
          <p>
            <b>Horário:</b> {formatScheduleDate(currentStart)} {" - "}
            {formatScheduleDate(currentEnd)}
            {" -> "}
            {formatScheduleDate(nextStart)} {" - "}
            {formatScheduleDate(nextEnd)}
          </p>
        </div>
      ),
      okText: "Confirmar",
      cancelText: "Cancelar",
      onOk: () =>
        saveChangedEvent(event, nextStart, nextEnd, nextAssemblerId),
      onCancel: rollbackTimelineChange,
    });
  };

  const handleMoveEvent = (
    eventId: number | string,
    newStartTime: number,
    newAssemblerId: number
  ) => {
    const numericEventId = Number(eventId);
    const event = items.find((item: any) => item.id === numericEventId);

    if (!event) return;

    const currentStart = getEventDate(event.start_time ?? event.start);
    const currentEnd = getEventDate(event.end_time ?? event.end);
    const durationMinutes = differenceInMinutes(currentEnd, currentStart);

    if (durationMinutes <= 0) {
      toast.error("O agendamento selecionado possui duração inválida");
      rollbackTimelineChange();
      return;
    }

    const nextStart = new Date(newStartTime);
    const nextEnd = addMinutes(nextStart, durationMinutes);

    confirmScheduleChange({
      event,
      nextStart,
      nextEnd,
      nextAssemblerId: newAssemblerId,
      title: "Confirmar reagendamento",
    });
  };

  const handleResizeEvent = (
    eventId: number | string,
    resizedTime: number,
    edge?: "left" | "right"
  ) => {
    const numericEventId = Number(eventId);
    const event = items.find((item: any) => item.id === numericEventId);

    if (!event) return;

    const currentStart = getEventDate(event.start_time ?? event.start);
    const currentEnd = getEventDate(event.end_time ?? event.end);
    const resizedDate = new Date(resizedTime);
    const nextStart = edge === "left" ? resizedDate : currentStart;
    const nextEnd = edge === "left" ? currentEnd : resizedDate;

    confirmScheduleChange({
      event,
      nextStart,
      nextEnd,
      nextAssemblerId: event.assemblerId,
      title: "Confirmar redimensionamento",
    });
  };

  const cancelSchedule = (eventId: number) => {
    if (!items) return;
    const event = items.find((event: any) => event.id === eventId);

    if (!event) return;
    api(token)
      .delete(`events/assemble/${eventId}`)
      .catch((error) => {
        console.log(error);
      })
      .then(async () => {
        await refreshCalendarData();
        toast.success("Agendamento cancelado com sucesso!");
        setOpenTicketDrawer(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Ocorreu um erro ao cancelar o agendamento");
      });
  };

  return (
    <div className="w-full overflow-hidden">
      <Modal
        title="Agendamento Avulso"
        open={isOpen}
        onCancel={onClose}
        footer={null}
      >
        <SingleSchedule
          onSuccess={onSuccessUpdateFront}
          onCancel={onClose}
          refetch={refetchEvents}
        />
      </Modal>

      {openSuggestionModal && (
        <Modal
          title="Sugestão de Angendamento"
          open={openSuggestionModal}
          onOk={() => setOpenSuggestionModal(false)}
          onCancel={() => {
            setPageSize(10);
            setOpenSuggestionModal(false);
          }}
          width={1200}
        >
          <div className="py-4">
            <SuggestSchedule onSchedule={() => refreshCalendarData()} />
          </div>
        </Modal>
      )}

      {openAgendaModal && (
        <Modal
          title="Agendar Ticket"
          open={openAgendaModal}
          width={800}
          footer={null}
          onCancel={() => {
            setOpenAgendaModal(false);
          }}
        >
          <ScheduleAssembleEvent
            onSchedule={handleOk}
            defaultDate={defaultScheduleTime}
            onCancel={() => setOpenAgendaModal(false)}
            initialTicket={agendaModalTicket}
            onAfterSchedule={handleAfterSchedule}
          />
        </Modal>
      )}
      <RelatedPendingScheduleTicketsModal
        open={showRelatedScheduleModal}
        taxId={relatedScheduleTaxId}
        excludeId={relatedScheduleExcludeId}
        onClose={() => setShowRelatedScheduleModal(false)}
        onScheduled={refreshCalendarData}
      />
      <h1>Calendário dos Montadores</h1>
      <Form
        name="assembly_calendar_filters"
        onFinish={searchTickets}
        className="w-12/12 m-auto bg-white p-5 rounded-lg"
      >
        <Form.Item
          className="w-4/12 mr-1"
          layout="vertical"
          name="assembler"
          rules={[{ required: false }]}
        >
          <div className="flex flex-row gap-10">
            <SearchAssemblers
              setAssembler={setAssembler}
              assembler={assembler}
              disabled={isFetchingEvents}
              onSearch={searchTickets}
            />
          </div>
        </Form.Item>
      </Form>
      <div className="flex w-full itens-center justify-end">
        <div className="flex">
          <div className="flex w-full justify-end p-4">
            <Button type="primary" onClick={onOpen} loading={ticketsLoading}>
              Agendamento Avulso
            </Button>
          </div>
          <div className="flex w-full justify-end p-4">
            <Badge count={tickets.total} overflowCount={999}>
              <Button
                type="primary"
                onClick={() => setOpenAgendaDrawer(true)}
                loading={ticketsLoading}
              >
                Agendar Manualmente
              </Button>
            </Badge>
          </div>
          <div className="flex w-full justify-end p-4">
            <Button
              type="primary"
              onClick={() => setOpenSuggestionModal(true)}
              loading={isFetchingEvents}
            >
              Sugerir Agendamento
            </Button>
          </div>
        </div>
      </div>
      {!isFetchingEvents && (
        <AgendaTimeline
          events={items}
          groups={groups}
          defaultAtualView={actualAgendaView}
          onEventDoubleClick={onEventDoubleClick}
          onEventMove={handleMoveEvent}
          onEventResize={handleResizeEvent}
          onSetAtualView={setActualAgendaView}
          enableItemMove={!movingEventId}
          enableGroupChange={!movingEventId}
          enableItemResize={!movingEventId}
        />
      )}
      <div>
        {openTicketDrawer && (
          <TicketDrawer
            ticketId={drawerTicket}
            open={openTicketDrawer}
            event={selectedEvent}
            buttons={[
              {
                text: "Cancelar Agendamento",
                danger: true,
                onClick: () => {
                  cancelSchedule(newEventId);
                },
              },
              ...(drawerTicket
                ? [
                    {
                      text: "Imprimir Agenda",
                      onClick: () => {
                        printSchedule(drawerTicket, token);
                      },
                    },
                  ]
                : []),
            ]}
            onClose={() => setOpenTicketDrawer(false)}
          />
        )}
        <Drawer
          size="large"
          title={
            <>
              {" "}
              <div className="flex justify-between">
                <div>Agendar</div>{" "}
                
                <div className="flex gap-4">
                  <Button
                    disabled={specificFilter === "normal"}
                    onClick={() => applySpecificFilter("normal")}
                  >
                    Normais
                  </Button>
                  <Button
                    disabled={specificFilter === "assist"}
                    onClick={() => applySpecificFilter("assist")}
                  >
                    Assistencia/Avulsos
                  </Button>
                  <Button
                    disabled={specificFilter === "highPriority"}
                    onClick={() => applySpecificFilter("highPriority")}
                  >
                    Alta Prioridade
                  </Button>
                  <Button
                    disabled={specificFilter === "unavailable"}
                    onClick={() => applySpecificFilter("unavailable")}
                  >
                    Em obras/indisponível
                  </Button>
                  <Button onClick={resetAgendaFilters}>Resetar</Button>
                </div>
                <div>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchTickets()}
                  >
                    Atualizar
                  </Button>
                </div>{" "}
              </div>{" "}
            </>
          }
          placement={"bottom"}
          closable={true}
          open={openAgendaDrawer}
          onClose={() => {
            resetAgendaFilters();
            setOpenAgendaDrawer(false);
          }}
        >
          <Table
            key={tableResetKey}
            dataSource={agendaTickets}
            columns={agendaCollumns}
            showSorterTooltip={{ target: "sorter-icon" }}
            onChange={(a, b, column) => {
              setSortingOrder({
                //@ts-ignore
                field: column.columnKey ?? "",
                //@ts-ignore
                order: column.order?.replace("end", "") ?? "",
              });
            }}
            pagination={{
              onChange: (page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize);
              },
              pageSize: pageSize,
              current: currentPage,
              total: tickets.total,
            }}
            loading={ticketsLoading}
          />
        </Drawer>
      </div>
    </div>
  );
};
